import { FC, useState } from 'react';
import { Row, Col, Card, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'hooks';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { APPOINTMENT_STATUSES } from 'constants/appointments';
import { fetchAppointmentsCharts } from 'services/api/analytics';
import { AppointmentsChartsResponse } from 'types/api/analytics';

import useOrgEffect from 'hooks/useOrgEffect';
import { Loading } from 'components/ui';
import CountCard from '../CountCard';
import { getStatusColor } from 'utils/appointments';
import AppointmentsDateChart from '../AnalyticDateChart';
import PieChart from '../PieChart';

const AnalyticsAppointments: FC = () => {
  const { t } = useTranslation();
  const { checkUserPermission } = usePermission();
  const [results, setResults] = useState<AppointmentsChartsResponse | null>(
    null
  );

  const [loading, setLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchAppointmentsCharts();
        if (!cancelled) setResults(res);
      } catch {
        // swallow
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const hasAppointmentReadPermission = checkUserPermission(
    PERMISSION_ROUTES.appointments,
    PERMISSION_ACTIONS.read
  );

  if (loading || !results) {
    return <Loading />;
  }

  return (
    <>
      <Row gutter={[16, 24]} style={{ marginBottom: 24 }}>
        <Col lg={{ span: 16 }}>
          <Card
            bodyStyle={{ padding: '20px 24px 16px' }}
            style={{ height: '100%' }}
          >
            <Typography.Title level={4} style={{ marginBottom: 20 }}>
              Appointment by dates
            </Typography.Title>
            <AppointmentsDateChart
              color="#4F84E8"
              data={Object.entries(results.appointments_month || {})?.map(
                ([key, value]) => ({
                  Date: `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
                  Count: (value as number) || 0
                })
              )}
            />
          </Card>
        </Col>
        <Col lg={{ span: 8 }}>
          <Card
            bodyStyle={{
              padding: '20px 24px 16px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            style={{ height: '100%' }}
          >
            <CountCard
              title="Total appointments"
              count={+(results.appointment_count || 0)}
              to={hasAppointmentReadPermission ? '/appointments' : null}
            >
              <PieChart
                id="appoitmentsPieChart"
                data={Object.entries(results?.appointments_status)?.map(
                  ([key, value]) => {
                    return {
                      status: t(`dashboard.${key}`),
                      value: Number(value),
                      color:
                        getStatusColor(
                          APPOINTMENT_STATUSES[
                            key as keyof typeof APPOINTMENT_STATUSES
                          ]
                        ) || '#808080'
                    };
                  }
                )}
              />
            </CountCard>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AnalyticsAppointments;
